import React, { useMemo, useState } from 'react';
import {
    Row, Col, Form, Collapse, Checkbox
} from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import StyledSelect from '../CustomAntdComponents/StyledSelect';
import StyledInput from '../CustomAntdComponents/StyledInput';
import {
    allowedAgedRange,
    formatOcrResponse,
    genderOptions,
    titleOptions,
    validateDOB,
    validateNoFutureDate,
    validatePassportExpiration
} from './helper';
import { useI18nContext } from '../../utils/I18n';
import translations from '../../screens/PassengerDetails/translations';
import {StyledParagraph, StyledTitle} from "../CustomAntdComponents/Typography";
import {useFlightSearchStore} from "../../store/flightSearch/flightSearchStore";
import moment from "moment";
import { getCountries } from 'country-state-picker';
import {
    CheckBoxLabel, CollapseWrapper, InfoWrapper,
    PrefillSectionWrapper, PrefillHeader, PrefillActions,
    StyledSavedProfileButton, StyledUploadButton,
    ProfileMenuItemWrapper, ProfileAvatar, ProfileInfo, ProfileName, ProfileDetails,
    SelectedProfileCard,
} from "./styles";
import {useStore} from "../../store/global/appStore";
import { validateNameRequired, validatePassportNumberRequired, validateRequired} from "../../utils/helpers";
import CustomDatePicker from "../CustomDatePicker";
import CustomDateInput from '../CustomDateInput';
import { Upload, message, Button, Dropdown } from 'antd';
import {ReactComponent as BulbIcon} from '../../assets/images/bulb-filled-icon.svg';
import { CameraOutlined, DownOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../store/auth/authStore';
import { Pen2 } from '@solar-icons/react';
import { uploadPassportOcrApi } from '../../utils/api';

 

const { Panel } = Collapse;

type Props = {
    field: any,
    passengerType: string,
    fieldName?: string,
}
function FlightPassengerForm({
                           field, passengerType, fieldName
                       }: Props) {
    const {
        state: { t },
    } = useI18nContext();

    const { airlines } = useStore();

    const airlinesOptions = useMemo(() => airlines.map((airline: Airline) => ({
        label: airline.Name,
        value: airline.Code,
    })), [airlines]);

    const {
        formData: {
            isDomestic
        },
    } = useFlightSearchStore();
    const countryOptions = useMemo(() => {
        const countries = getCountries().map(({ name, code }: any) => ({
            label: name,
            value: code,
        }));

        const priorities = [
            {
                label: "Nigeria",
                value: "ng",
            },
            {
                label: "Kenya",
                value: "ke",
            }
        ]
        const others = countries.filter((c) => c.label !== "Kenya" && c.label !== "Nigeria");

        return [
            ...priorities,
            ...others
        ];
    }, []);

    const isAdult = passengerType === 'ADT';
    const form = Form.useFormInstance();

    const [isUploadingOcr, setIsUploadingOcr] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const passportExpirationValue = Form.useWatch([fieldName || 'adults', field.name, 'passportExpiration'], form);

    const warningText = useMemo(() => {
    if (!passportExpirationValue || !moment.isMoment(passportExpirationValue) || !passportExpirationValue.isValid()) {
        return null;
    }
    const today = moment().startOf('day');

    // expired or expiring today — flag hard rather than silently dropping
    if (!passportExpirationValue.isAfter(today)) {
        return 'Your passport has expired';
    }

    const monthsDiff = passportExpirationValue.diff(today, 'months', true);
    if (monthsDiff > 6) {
        return null;
    }

    const wholeMonths = Math.floor(monthsDiff);
    if (wholeMonths < 1) {
        const days = passportExpirationValue.diff(today, 'days');
        return `!!! Your passport expires in ${days} day${days !== 1 ? 's' : ''}`;
    }
    return `!!! Your passport expires in ${wholeMonths} month${wholeMonths > 1 ? 's' : ''}`;
}, [passportExpirationValue]);

    const handleOCR = async (file: any) => {
        setErrorMessage("");
        setIsUploadingOcr(true);
        message.loading({ content: 'Extracting data...', key: 'ocr' });
        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await uploadPassportOcrApi(formData);

            if (response.status !== 200) {
                const errorResponse= response?.data?.detail || response?.data?.Message || 'Failed to extract passport data';
                message.error({ content: message, key: 'ocr' });
                setErrorMessage(errorResponse);
                return false;
            }
            setErrorMessage("");
            const ocr = response.data?.Data;
            const errors = response.data?.Errors;

            if (errors && errors.length > 0) {
                message.warning({ content: errors.join(', '), key: 'ocr' });
            }

            const ocrData = formatOcrResponse(ocr);

            const currentValues = form.getFieldsValue();
            const arrayName = fieldName || 'adults';
            const passengers = [...(currentValues[arrayName] || [])];

            if (passengers[field.name]) {
                passengers[field.name] = { ...passengers[field.name], ...ocrData };
            } else {
                passengers[field.name] = ocrData;
            }

            form.setFieldsValue({ [arrayName]: passengers });
            message.success({ content: 'Passport data extracted!', key: 'ocr' });
        } catch (error: any) {
            const errMsg = error?.data?.Message || error?.message || 'Failed to process passport data';
            setErrorMessage(errMsg);
            message.error({ content: errMsg, key: 'ocr' });
        } finally {
            setIsUploadingOcr(false);
        }

        return false;
    };

    const { isLoggedIn, travelers } = useAuthStore();
    const [selectedProfile, setSelectedProfile] = useState<any>(null);

    const profiles = useMemo(() => {
      if (!travelers || travelers.length === 0) return [];
      return travelers.map((item: any) => ({
        label: `${item.FirstName} ${item.LastName}`,
        value: item.Id,
        data: {
          firstName: item.FirstName,
          lastName: item.LastName,
          dob: item.DOB ? moment(item.DOB) : undefined,
          title: item.Title,
          gender: item.Gender === 0 ? 'male' : 'female',
          passportNumber: item.PassportNumber || '',
          issuanceCountry: item.IssuanceCountry ? item.IssuanceCountry.toLowerCase() : undefined,
          issuanceDate: item.PassportIssuedDate ? moment(item.PassportIssuedDate) : undefined,
          passportExpiration: item.PassportExpiryDate ? moment(item.PassportExpiryDate) : undefined,
          email: item.Email || '',
          frequentFlyerAirline: item.FrequentFlyerAirline || undefined,
          frequentFlyerNumber: item.FrequentFlyerPhone || '',
          primary: item.IsPrimary
        }
      }));
    }, [travelers]);

    const handleProfileSelect = (value: string) => {
      const profile = profiles.find(p => p.value === value);
      if (profile) {
        setSelectedProfile(profile);
        const currentValues = form.getFieldsValue();
        const arrayName = fieldName || 'adults';
        const passengers = [...(currentValues[arrayName] || [])];
        
        if (passengers[field.name]) {
            passengers[field.name] = {
                ...passengers[field.name],
                ...profile.data
            };
        } else {
            passengers[field.name] = profile.data;
        }
        form.setFieldsValue({ [arrayName]: passengers });
      }
    };

    const profileMenu = {
      items: profiles.length > 0
        ? profiles.map(p => ({
            key: p.value,
            label: (
              <ProfileMenuItemWrapper>
                <ProfileAvatar>
                  {p.data.firstName ? p.data.firstName[0] : ''}{p.data.lastName ? p.data.lastName[0] : ''}
                </ProfileAvatar>
                <ProfileInfo>
                  <ProfileName>
                      {p.data.firstName} {p.data.lastName} {p.data.primary && <span>(Primary traveller)</span>}
                  </ProfileName>
                  <ProfileDetails>
                    {p.data.email ? `${p.data.email} • ` : ''}
                    {p.data.dob ? p.data.dob.format('MMM DD, YYYY') : ''}
                  </ProfileDetails>
                </ProfileInfo>
              </ProfileMenuItemWrapper>
            )
          }))
        : [
            {
              key: 'no-data',
              disabled: true,
              label: (
                <div style={{ padding: '8px 12px', color: '#9CA3AF', textAlign: 'center', fontSize: '13px' }}>
                  No saved profiles found
                </div>
              )
            }
          ],
      onClick: ({ key }: any) => {
        if (key !== 'no-data') {
          handleProfileSelect(key);
        }
      }
    };

    return (
        <Row gutter={16}>
            <Col span={24}>
                <div style={{ marginBottom: "20px" }}>
                    {selectedProfile ? (
                        <SelectedProfileCard>
                            <ProfileMenuItemWrapper style={{ padding: 0 }}>
                                <ProfileAvatar>
                                    {selectedProfile.data.firstName ? selectedProfile.data.firstName[0] : ''}{selectedProfile.data.lastName ? selectedProfile.data.lastName[0] : ''}
                                </ProfileAvatar>
                                <ProfileInfo>
                                    <ProfileName>
                                        {selectedProfile.data.firstName} {selectedProfile.data.lastName} {selectedProfile.data.primary && <span>(Primary traveller)</span>}
                                    </ProfileName>
                                    <ProfileDetails>
                                        {selectedProfile.data.email ? `${selectedProfile.data.email} • ` : ''}
                                        {selectedProfile.data.dob ? selectedProfile.data.dob.format('MMM DD, YYYY') : ''}
                                    </ProfileDetails>
                                </ProfileInfo>
                            </ProfileMenuItemWrapper>
                            <Button type="text" icon={<Pen2 size={24} color="#6B7280" />} onClick={() => setSelectedProfile(null)} />
                        </SelectedProfileCard>
                    ) : (
                        <PrefillSectionWrapper className={`${errorMessage ? 'has-error' : ''}`}>
                            <PrefillHeader>
                                <BulbIcon className={`${errorMessage ? 'has-error' : ''}`} />
                                <StyledTitle level={4} className={`${errorMessage ? 'has-error' : ''}`} >{errorMessage ? "Image upload error" : "Pre-fill your details"}</StyledTitle>
                            </PrefillHeader>
                            <StyledParagraph size="default" style={{ color: '#6B7280', fontSize: '13px' }}>
                                {errorMessage ? errorMessage :isLoggedIn 
                                    ? "Upload your passport data page or use your saved profile and we will pre-fill your data for you. Recommended format: JPEG, PNG"
                                    : "Upload your passport data page and we will pre-fill your data for you. Recommended format: JPEG, PNG"}
                            </StyledParagraph>
                            <PrefillActions>
                                {isLoggedIn && (
                                    <Dropdown menu={profileMenu} trigger={['click']} placement="bottomLeft">
                                        <StyledSavedProfileButton shape="round">
                                            Use saved profile <DownOutlined style={{ fontSize: '12px' }} />
                                        </StyledSavedProfileButton>
                                    </Dropdown>
                                )}
                                <Upload showUploadList={false} beforeUpload={handleOCR} accept="image/*" disabled={isUploadingOcr}>
                                    <StyledUploadButton shape="round" loading={isUploadingOcr}>
                                        {errorMessage ? "Upload again" : isUploadingOcr ? "Uploading..." : isLoggedIn ? "Upload passport" : "Upload"}
                                    </StyledUploadButton>
                                </Upload>
                            </PrefillActions>
                        </PrefillSectionWrapper>
                    )}
                </div>
            </Col>

            <Col span={12}>
                <Form.Item
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...field}
                    name={[field.name, 'title']}
                    fieldKey={[field.key, 'title']}
                    rules={[{ required: true, message: t(translations).validations.title }]}
                >
                    <StyledSelect options={titleOptions} placeholder={t(translations).input.title} />
                </Form.Item>
            </Col>

             <Col span={12}>
               <Form.Item
                 {...field}
                 name={[field.name, 'gender']}
                 fieldKey={[field.key, 'gender']}
                 rules={[{ required: true, message: 'Please select gender' }]}
               >
                 <StyledSelect options={genderOptions} placeholder="Gender" />
               </Form.Item>
             </Col>

            <Col span={24}>
                <Form.Item
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...field}
                    name={[field.name, 'firstName']}
                    fieldKey={[field.key, 'firstName']}
                    rules={[{ validator: validateNameRequired }]}
                >
                    <StyledInput placeholder={t(translations).input.firstName} />
                </Form.Item>
            </Col>

            <Col span={24}>
                <Form.Item
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...field}
                    name={[field.name, 'lastName']}
                    fieldKey={[field.key, 'lastName']}
                    rules={[{ validator: validateNameRequired }]}
                >
                    <StyledInput placeholder={t(translations).input.lastName} />
                </Form.Item>
            </Col>

            <Col span={24}>
                <Form.Item
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...field}
                    name={[field.name, 'dob']}
                    fieldKey={[field.key, 'dob']}
                    rules={[
                        {
                            validator: (_, value: any) => {
                                return validateDOB(value, passengerType);
                            }
                        }
                    ]}
                >
                    {/* <CustomDatePicker label={t(translations).input.dob} onChange={(val) => val } /> */}
                    <CustomDateInput label={t(translations).input.dob} onChange={(val) => val } />
                </Form.Item>
            </Col>

            {
                !isDomestic && (
                    <>
                        <Col span={24}>
                            <div style={{ marginBottom: "20px"}}>
                                <StyledTitle level={4}>
                                    Passport details
                                </StyledTitle>
                            </div>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Form.Item
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...field}
                                name={[field.name, 'passportNumber']}
                                fieldKey={[field.key, 'passportNumber']}
                                rules={[
                                    { validator: validatePassportNumberRequired },
                                ]}
                            >
                                <StyledInput placeholder={t(translations).input.passportNumber} />
                            </Form.Item>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Form.Item
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...field}
                                name={[field.name, 'issuanceCountry']}
                                fieldKey={[field.key, 'issuanceCountry']}
                                rules={[{ required: true, message: t(translations).validations.issuanceCountry }]}
                            >
                                <StyledSelect
                                    showSearch
                                    options={countryOptions}
                                    placeholder={t(translations).input.issuanceCountry}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24}>
                            <Form.Item
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...field}
                                name={[field.name, 'issuanceDate']}
                                rules={[
                                    {
                                        validator: (_, value: any) => {
                                            return validateNoFutureDate(value);
                                        }
                                    }
                                ]}
                            >
                                <CustomDateInput label={t(translations).input.passportIssuanceDate} onChange={(val) => val } />
                                {/* <CustomDatePicker label={t(translations).input.passportIssuanceDate} onChange={(val) => val } /> */}
                            </Form.Item>
                        </Col>

                        <Col xs={24}>
                            <Form.Item
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...field}
                                name={[field.name, 'passportExpiration']}
                                rules={[
                                    {
                                        validator: (_, value: any) => {
                                            return validatePassportExpiration(value);
                                        }
                                    }
                                ]}
                                style={{ marginBottom: warningText ? '10px' : undefined }}
                            >
                                <CustomDateInput label={t(translations).input.passportExpiration} onChange={(val) => val } startYearIndex={30} />
                            </Form.Item>
                            {warningText && (
                                <div style={{ color: '#d97706', fontSize: '12px', marginTop: '-1px', marginBottom: '20px', fontWeight: 500 }}>
                                    {warningText}
                                </div>
                            )}
                        </Col>
                    </>
                )
            }

            <Col span={24}>
                <div style={{ marginBottom: "20px"}}>
                    <StyledTitle level={4}>
                        Additional information <span>(optional)</span>
                    </StyledTitle>
                </div>

                <CollapseWrapper>
                    <Collapse>
                        <Panel header="Frequent flyer? (optional)" key="1">
                            <Form.Item
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...field}
                                name={[field.name, 'frequentFlyerAirline']}
                                fieldKey={[field.key, 'frequentFlyerAirline']}
                            >
                                <StyledSelect
                                    showSearch
                                    allowClear
                                    options={airlinesOptions}
                                    placeholder={t(translations).input.frequentFlyerAirline}
                                />
                            </Form.Item>

                            <Form.Item
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...field}
                                name={[field.name, 'frequentFlyerNumber']}
                                fieldKey={[field.key, 'frequentFlyerNumber']}
                            >
                                <StyledInput placeholder={t(translations).input.frequentFlyerNumber} />
                            </Form.Item>
                        </Panel>
                    </Collapse>
                </CollapseWrapper>
            </Col>

            {
                (field.key === 0  && isAdult)&& (
                    <Col span={24}>
                        <Form.Item
                            /* eslint-disable-next-line react/jsx-props-no-spreading */
                            {...field}
                            valuePropName="checked"
                            name={[field.name, 'savePassengerData']}
                            fieldKey={[field.key, 'savePassengerData']}
                        >
                            <Checkbox>
                                <CheckBoxLabel>
                                    Save my data for future purposes
                                </CheckBoxLabel>
                            </Checkbox>
                        </Form.Item>
                    </Col>
                )
            }
        </Row>
    );
}

export default FlightPassengerForm;
